import { useEffect, useState, useCallback } from 'react';

import { DIRECTION_OPTIONS, SORT_OPTIONS } from '../../utils/hooks/useSort';
import { defaultMobileDeviceSortingOrder, MobileDevice } from '../mobileDevices';
import { Filter } from '../../utils/FilterTypes';
import { loadMobileDevices as loadMobileDevicesApi } from '../../apiClient';
import { errorAction } from '../../utils/notifications';

export const useMobileDevicesList = () => {
    // State management with React hooks
    const [mobileDevices, setMobileDevices] = useState<MobileDevice[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [queryOptions, setQueryOptions] = useState<SORT_OPTIONS>(defaultMobileDeviceSortingOrder);
    const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
    const [activePage, setActivePage] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);

    // Reset to first page when sort options or filters change
    useEffect(() => {
        setActivePage(1);
    }, [queryOptions, activeFilters]);

    // Load mobile devices when dependencies change
    const loadMobileDevices = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await loadMobileDevicesApi(queryOptions, activePage, activeFilters);
            setMobileDevices(response.list);
            setTotal(response.total);
        } catch (error) {
            errorAction('Failed to load mobile devices');
            setError('Failed to load mobile devices');
            setMobileDevices([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [queryOptions, activePage, activeFilters]);

    useEffect(() => {
        loadMobileDevices();
    }, [loadMobileDevices]);

    // Handle sorting
    const handleSort = ([column, direction]: [string, DIRECTION_OPTIONS]) => {
        if (column) {
            setQueryOptions({
                'orderBy:column': columnToAttributeMapping[column],
                'orderBy:direction': direction,
            });
        } else {
            setQueryOptions(defaultMobileDeviceSortingOrder);
        }
    };

    return {
        handleSort,
        mobileDevices,
        loading,
        error,
        queryOptions,
        activeFilters,
        setActiveFilters,
        activePage,
        setActivePage,
        total,
        setTotal,
        refresh: loadMobileDevices
    };
};

const columnToAttributeMapping: Record<string, keyof MobileDevice> = {
    'ID': 'id',
    'Hardware ID': 'imei',
    'Status': 'status',
    'APK Type': 'apkType',
    'Created At': 'createdAt',
    'Vendor/Partner': 'vendorName',
    'Outlet': 'outlet',
    'Login Attempts': 'passwordAttempts',
    'Blocked': 'blocked',
    'Last TX At': 'lastTransactionAt',
};
